import { createListProjectionRuntime } from 'flare-chat-core';
import { mergeSessionListRecords } from 'flare-chat-core/contracts/session-list-window';
import { resolveProjectRecordId } from './projectProjection.js';

const EMPTY = Object.freeze({ items: [], loading: true, loadingMore: false, error: null,
  updatedAt: null, canRenderSessions: true, window: {} });
const INVALID_CANONICAL_PROJECTION = Object.freeze(Object.assign(
  new TypeError('invalid_session_project_projection'), { code: 'INVALID_SESSION_PROJECT_PROJECTION', retryable: false },
));
const projectAvailable = (projects, id) => projects.some(project => (
  resolveProjectRecordId(project) === id && project.status !== 'archived'
));
const validPageSize = value => Number.isInteger(value) && value > 0 && value <= 100;
const sameProject = (record, projectId) => (
  (!record?.project_id && !record?.projectId)
  || String(record.project_id ?? record.projectId).trim() === projectId
);
const validSessionItems = (items, projectId) => (
  Array.isArray(items) && items.every(item => item && typeof item === 'object'
    && !Array.isArray(item) && sameProject(item, projectId)
    && mergeSessionListRecords([], [item]).length === 1)
);
const validCanonicalProjection = (projection, projectId, scopeKey) => (
  projection?.scopeKey === JSON.stringify([scopeKey, projectId])
  && Number.isInteger(projection.revision) && projection.revision >= 0
  && ['initializing', 'ready', 'stale', 'refreshing', 'degraded', 'unavailable'].includes(projection.status)
  && (projection.updatedAt === null || Number.isFinite(projection.updatedAt))
  && validSessionItems(projection.items, projectId)
);
const displayProjection = projection => {
  const accepted = Number.isFinite(projection.updatedAt);
  const pending = projection.status === 'initializing' || projection === EMPTY;
  return { ...projection, loading: pending && !accepted, canRenderSessions: accepted || pending };
};

/** Core owns the active list; public ListProjectionRuntime owns background cache and IO. */
export const createSidebarSessionReader = ({
  sessionAPI, scope, projects = [], onChange = () => {}, freshnessMs = 15000, retryDelaysMs,
  currentProjection = () => null, canonicalActions = {}, pageSize,
}) => {
  if (!validPageSize(pageSize)) throw new TypeError('pageSize must be an integer from 1 to 100');
  const entries = new Map();
  let canonicalProjects = projects;
  let canonicalProjectId = '';
  let disposed = false;
  const read = projectId => async ({ append, current, signal }) => {
    const response = await sessionAPI.list({
      project_id: projectId, user_id: scope.userId, instance_id: scope.instanceId,
      domain_pack_domain: scope.domainPackDomain, domain_pack_version: scope.domainPackVersion,
      status: 'active', include_context: false, page_size: pageSize,
      cursor: append ? current.window.nextCursor : '',
    }, { signal });
    if (!validSessionItems(response?.sessions, projectId)) throw new TypeError('invalid_session_project_projection');
    return { items: append ? mergeSessionListRecords(current.items, response.sessions) : response.sessions,
      window: response.window };
  };
  const entryFor = projectId => {
    if (entries.has(projectId)) return entries.get(projectId);
    const reader = read(projectId);
    const runtime = createListProjectionRuntime({
      freshnessMs: { default: freshnessMs }, retryDelaysMs, readers: { sessions: reader },
    });
    const entry = { runtime, reader, active: false, canonicalRevision: null, unsubscribe: runtime.subscribe(onChange) };
    entries.set(projectId, entry);
    return entry;
  };
  const bindPassive = (entry, projectId) => {
    // A different public scope cancels background requests before Core takes ownership.
    entry.unsubscribe();
    void entry.runtime.bindScope({ key: `${scope.key}:closed`, enabled: false }).catch(() => {});
    void entry.runtime.bindScope({ key: scope.key, projectId, enabled: false }).catch(() => {});
    entry.unsubscribe = entry.runtime.subscribe(onChange);
  };
  const activate = (entry, projectId) => {
    // Availability blocks bind's implicit read; the requested command owns the single IO.
    entry.runtime.setReaders({ sessions: undefined });
    void entry.runtime.bindScope({ key: scope.key, projectId }).catch(() => {});
    entry.runtime.setReaders({ sessions: entry.reader });
    return entry.runtime;
  };
  const bindCanonicalProjection = (projectId, projection = {}) => {
    if (disposed) return;
    const previousId = canonicalProjectId;
    canonicalProjectId = projectAvailable(canonicalProjects, projectId) ? projectId : '';
    if (!canonicalProjectId) return;
    const entry = entryFor(projectId);
    if (previousId !== projectId) { bindPassive(entry, projectId); entry.canonicalRevision = null; }
    if (!validCanonicalProjection(projection, projectId, scope.key)
      || projection.status !== 'ready' || !Number.isFinite(projection.updatedAt)
      || entry.canonicalRevision === projection.revision) return;
    entry.canonicalRevision = projection.revision;
    entry.runtime.reconcile('sessions', projection.items, { window: projection.window });
  };
  const open = async projectId => {
    if (disposed) return;
    if (!projectAvailable(canonicalProjects, projectId)) throw new Error('project_not_available');
    const entry = entryFor(projectId);
    entry.active = true;
    if (projectId === canonicalProjectId) return entry.runtime.getSnapshot();
    return activate(entry, projectId).refresh({ resources: ['sessions'] });
  };
  const close = projectId => {
    const entry = entries.get(projectId);
    if (!entry?.active) return;
    entry.active = false;
    if (projectId === canonicalProjectId) return;
    void entry.runtime.bindScope({ key: `${scope.key}:closed`, enabled: false }).catch(() => {});
  };
  const command = (projectId, name) => {
    const entry = entries.get(projectId);
    if (!entry?.active || disposed) return Promise.resolve();
    if (projectId === canonicalProjectId) return canonicalActions[name]?.() ?? Promise.resolve();
    const runtime = activate(entry, projectId);
    return name === 'loadMore' ? runtime.loadMore('sessions') : runtime.refresh({ force: true, resources: ['sessions'] });
  };
  const backgroundEntries = () => [...entries.entries()]
    .filter(([projectId, entry]) => entry.active && projectId !== canonicalProjectId)
    .map(([projectId, entry]) => activate(entry, projectId));
  const getProjection = projectId => {
    const cached = entries.get(projectId)?.runtime.getSnapshot().sessions ?? EMPTY;
    const canonical = projectId === canonicalProjectId ? currentProjection(projectId) : null;
    if (!validCanonicalProjection(canonical, projectId, scope.key)) {
      if (canonical?.scopeKey !== JSON.stringify([scope.key, projectId])) return displayProjection(cached);
      // An invalid same-scope input is a boundary failure, never a ready empty result.
      return displayProjection({ ...cached, error: INVALID_CANONICAL_PROJECTION, loading: false,
        loadingMore: false, status: Number.isFinite(cached.updatedAt) ? 'degraded' : 'unavailable' });
    }
    // A Core reset may not erase an already accepted background page while it reloads.
    if (!Number.isFinite(canonical.updatedAt) && Number.isFinite(cached.updatedAt)) {
      return displayProjection({ ...cached, error: canonical.error, loading: canonical.loading,
        loadingMore: canonical.loadingMore, status: canonical.status });
    }
    return displayProjection(canonical);
  };
  return {
    open, close, bindCanonicalProjection, getProjection,
    refresh: projectId => command(projectId, 'refresh'),
    loadMore: projectId => command(projectId, 'loadMore'),
    invalidate: () => Promise.all(backgroundEntries().map(runtime => runtime.invalidate('sessions'))),
    refreshIfStale: () => Promise.all(backgroundEntries().map(runtime => runtime.refresh({ resources: ['sessions'] }))),
    updateProjects: next => {
      canonicalProjects = next;
      [...entries.keys()].filter(id => !projectAvailable(next, id)).forEach(id => {
        const entry = entries.get(id); entry.unsubscribe(); entry.runtime.dispose(); entries.delete(id);
      });
    },
    dispose: () => {
      disposed = true;
      entries.forEach(entry => { entry.unsubscribe(); entry.runtime.dispose(); });
      entries.clear();
    },
  };
};
